import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  StripeForm,
  TranslationProvider,
  getTranslation,
  Spacer,
  STRIPE_FORM_CONTEXT,
} from '@resy/ui-kitchen';

import WidgetNav from '../WidgetNav';
import WidgetPage, {
  WidgetPageBody,
} from '../WidgetPage';

import WidgetTitle from '../WidgetTitle';
import OnScrollView from '../OnScrollView';
import './AddPaymentPage.scss';
import PAYMENT from '../../constants/pageTypes';
import OPENAI_WIDGET from '../../constants/srcTypes';

const addPaymentPagePropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  AutomationMode: PropTypes.bool.isRequired,
  backPath: PropTypes.string.isRequired,
  bookingState: PropTypes.shape({
    bookOnBehalfOf: PropTypes.bool,
    details: PropTypes.shape({
      cancellation: PropTypes.bool,
      payment: PropTypes.shape({
        amounts: PropTypes.shape({
          total: PropTypes.number,
        }),
      }),
    }),
    nonce: PropTypes.string,
  }).isRequired,
  bookingThunks: PropTypes.shape({
    setNonce: PropTypes.func,
    setPaymentId: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
  navSearch: PropTypes.shape({
    date: PropTypes.string,
    seats: PropTypes.number,
  }).isRequired,
  sourceId: PropTypes.string,
  stripePublishableKey: PropTypes.string,
  venueInfo: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    images: PropTypes.arrayOf(PropTypes.string),
    partnershipBranding: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
    }),
  }).isRequired,
  viewPortHeight: PropTypes.number,
};

class AddPaymentPage extends React.Component {
  static propTypes = addPaymentPagePropTypes;

  static defaultProps = {
    sourceId: undefined,
    stripePublishableKey: undefined,
    viewPortHeight: undefined,
  }

  state = {
    titleOpacity: 0,
  }

  scrollFunc = (elems, scrollTop) => {
    const diffWithScrollTop = (elems[0].offsetTop + elems[0].offsetHeight) - scrollTop;
    const perc = 1 - diffWithScrollTop / elems[0].offsetHeight;

    this.setState({
      titleOpacity: (perc <= 0) ? 0 : 1,
    });
  }

  getScrollEvents = () => [
    {
      selector: ['.WidgetPageBody'],
      func: this.scrollFunc,
    },
  ]

  handleConciergeReservation = (nonce) => {
    const {
      bookingThunks: {
        setNonce,
      },
      backPath,
    } = this.props;

    setNonce(nonce);

    window.location.hash = backPath;
  }

  renderWidgetNav = () => {
    const {
      Analytics,
      backPath,
      navigate,
      navSearch,
    } = this.props;

    const { titleOpacity } = this.state;

    return (
      <WidgetNav
        Analytics={Analytics}
        backPath={backPath}
        navigate={navigate}
        showBack
        showProfileMenu
        search={navSearch}
        type={PAYMENT}
        title={getTranslation({ translationKey: 'add_credit_card', fallback: 'Add Credit Card' })}
        titleOpacity={titleOpacity}
      />
    );
  }

  renderWidgetTitle = () => (
    <>
      <WidgetTitle horizontalRule={false}>
        <TranslationProvider translationKey="add_credit_card">
          <span>Add Credit Card</span>
        </TranslationProvider>
      </WidgetTitle>
      <TranslationProvider translationKey="select_credit_card_desc">
        <p>
          This venue requires a credit card to book because this reservation is either pre-paid, has
          pre-paid items, or may have cancellation fee.
        </p>
      </TranslationProvider>
    </>
  )

  closeStripeAddCard = (paymentId) => {
    const { bookingState } = this.props;
    if (bookingState.bookOnBehalfOf) this.handleConciergeReservation(paymentId);
    else this.loadStripeNextStep();
  }

  loadStripeNextStep = () => {
    const { backPath } = this.props;
    window.location.hash = backPath;
  }

  renderPaymentForm = () => {
    const {
      Analytics,
      AutomationMode,
      matchMedia,
      stripePublishableKey,
      bookingState,
      venueInfo,
      backPath,
      sourceId,
    } = this.props;

    const context = bookingState.bookOnBehalfOf
      ? STRIPE_FORM_CONTEXT.concierge : STRIPE_FORM_CONTEXT.booking;

    const paymentFormClassName = sourceId === OPENAI_WIDGET ? 'AddPaymentPage__paymentForm--no-top-padding' : '';

    return (
      <div className={paymentFormClassName}>
        <WidgetPageBody>
          <StripeForm
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            closeForm={this.closeStripeAddCard}
            matchMedia={matchMedia}
            redirectPath={backPath}
            stripePublishableKey={stripePublishableKey}
            context={context}
            venueId={venueInfo.id}
          />
        </WidgetPageBody>
      </div>
    );
  }

  render() {
    const { sourceId, viewPortHeight, venueInfo } = this.props;
    const spacerHeight = sourceId === OPENAI_WIDGET ? '0' : '60';

    if (!venueInfo) return null;

    return (
      <WidgetPage className="AddPaymentPage" height={viewPortHeight} ariaLabel={getTranslation({ translationKey: 'add_credit_card', fallback: 'Add Credit Card' })}>
        {this.renderWidgetNav()}
        <OnScrollView className="AddPaymentPage__ScrollView" scrollEvents={this.getScrollEvents()}>
          <Spacer height={spacerHeight} />
          {this.renderPaymentForm()}
        </OnScrollView>
      </WidgetPage>
    );
  }
}

AddPaymentPage.displayName = 'AddPaymentPage';

const mapStateToProps = ({ resyRouter }) => ({
  navSearch: resyRouter.search,
});

export { addPaymentPagePropTypes, AddPaymentPage as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps)(AddPaymentPage);
